// Vista admin /bookings/ — lista bookings del mes con filtros + acción "Mark Completed".
//
// NABU-71 acceptance criterion:
//   "Vista admin /bookings/ en Orion-Dashboard: lista bookings del mes,
//    filtros por status, acción 'Mark Completed' que emite event
//    booking_completed y append-only crea apps/time_tracking/TimeEntry"

import { useMemo, useState } from 'react';

import {
  useBookings,
  useCancelBooking,
  useMarkCompleted,
} from '../hooks/useBookings';
import type { BookingStatus } from '../types';

const STATUS_OPTIONS: { value: BookingStatus | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'pending_confirmation', label: 'Pendiente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'completed', label: 'Completado' },
  { value: 'canceled', label: 'Cancelado' },
  { value: 'no_show', label: 'No-show' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function BookingsList() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [status, setStatus] = useState<BookingStatus | ''>('');

  const filters = useMemo(
    () => ({ year, month, status: status || undefined }),
    [year, month, status],
  );
  const { data: bookings, isLoading, error } = useBookings(filters);
  const markCompleted = useMarkCompleted();
  const cancel = useCancelBooking();

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">Reservas 1:1</h1>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-muted-foreground">
          Mes:
          <select
            className="ml-2 px-2 py-1 rounded border bg-background"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2000, i, 1).toLocaleString('es-MX', { month: 'long' })}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-muted-foreground">
          Año:
          <input
            type="number"
            className="ml-2 px-2 py-1 rounded border bg-background w-24"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>
        <label className="text-sm text-muted-foreground">
          Estado:
          <select
            className="ml-2 px-2 py-1 rounded border bg-background"
            value={status}
            onChange={(e) => setStatus(e.target.value as BookingStatus | '')}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && <div className="text-muted-foreground">Cargando…</div>}
      {error && (
        <div className="text-red-500">
          Error: {(error as Error).message}
        </div>
      )}

      {bookings && bookings.length === 0 && (
        <div className="text-muted-foreground">
          No hay reservas en este mes con los filtros seleccionados.
        </div>
      )}

      {bookings && bookings.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-2 pr-2">Código</th>
              <th className="py-2 pr-2">Cliente</th>
              <th className="py-2 pr-2">Email</th>
              <th className="py-2 pr-2">Inicio</th>
              <th className="py-2 pr-2">Estado</th>
              <th className="py-2 pr-2">Acción</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} className="border-t border-border">
                <td className="py-2 pr-2 font-mono text-xs">{b.confirmation_code}</td>
                <td className="py-2 pr-2">{b.attendee_name}</td>
                <td className="py-2 pr-2">{b.attendee_email}</td>
                <td className="py-2 pr-2">{formatDate(b.start_at)}</td>
                <td className="py-2 pr-2">
                  <span
                    className={`px-2 py-0.5 rounded text-xs ${statusBadgeClass(
                      b.status,
                    )}`}
                  >
                    {b.status}
                  </span>
                </td>
                <td className="py-2 pr-2 space-x-2">
                  {(b.status === 'confirmed' || b.status === 'pending_confirmation') && (
                    <button
                      type="button"
                      onClick={() => markCompleted.mutate(b.id)}
                      disabled={markCompleted.isPending}
                      className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                    >
                      Mark Completed
                    </button>
                  )}
                  {b.status !== 'canceled' && b.status !== 'completed' && (
                    <button
                      type="button"
                      onClick={() => cancel.mutate({ id: b.id })}
                      disabled={cancel.isPending}
                      className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:text-foreground"
                    >
                      Cancelar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function statusBadgeClass(s: BookingStatus): string {
  switch (s) {
    case 'pending_confirmation':
      return 'bg-amber-900 text-amber-100';
    case 'confirmed':
      return 'bg-blue-900 text-blue-100';
    case 'completed':
      return 'bg-emerald-900 text-emerald-100';
    case 'canceled':
      return 'bg-red-900 text-red-100';
    case 'no_show':
      return 'bg-zinc-700 text-zinc-100';
    default:
      return 'bg-zinc-700 text-zinc-100';
  }
}
